import { cn } from '@/lib/utils'

export type ProductImageGalleryEditorProps = {
  urls: string[]
  onRemove: (url: string) => void
  className?: string
  /** Shown above the grid when there is at least one image */
  caption?: string
}

/**
 * Thumbnail grid for product image URLs with remove actions. Order matches `urls` (storefront order).
 */
export function ProductImageGalleryEditor({
  urls,
  onRemove,
  className,
  caption = 'Current images — remove any you no longer need. New uploads are added below.',
}: ProductImageGalleryEditorProps) {
  if (!urls.length) return null

  return (
    <div className={cn('space-y-3', className)}>
      <p className="text-xs text-muted-foreground">{caption}</p>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {urls.map((url, index) => (
          <li
            key={`${index}-${url}`}
            className="group relative aspect-square overflow-hidden rounded-xl border border-border bg-muted/50 shadow-sm"
          >
            <img
              src={url}
              alt=""
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-80 transition-opacity group-hover:opacity-100" />
            <button
              type="button"
              className="absolute right-2 top-2 flex h-8 min-w-[2rem] items-center justify-center rounded-full bg-background/95 px-2 text-xs font-semibold text-destructive shadow-md ring-1 ring-border/80 transition-colors hover:bg-destructive/15"
              onClick={() => onRemove(url)}
            >
              Remove
            </button>
            <span className="pointer-events-none absolute bottom-2 left-2 rounded bg-background/85 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground">
              {index + 1}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
